import type { BallStateSnapshot } from './BallState';
import type { ShotSimulationBuildInfo } from './ShotSimulation';
import type { HoleScore, RoundStatus } from './RoundScore';
import type { ClubDefinition } from './Club';
import type { TrajectoryDefinition, TrajectoryId } from './Trajectory';
import type { WindSnapshot } from './WindModel';
import type { ActiveCareerTournament, SeasonEvent } from '../career/types';
import './gameplay-hud.css';
import { setSheetOpen } from '../ui/SheetVisibility';
import {
  captureLeaderboardSnapshot,
  movementMarker,
  movementSince,
} from '../career/TournamentBroadcast';

export type ShotDebugPhase = 'waiting' | 'flying' | 'rolling' | 'resting';

export interface ShotDebugHudState {
  readonly phase: ShotDebugPhase;
  readonly aimDegrees: number;
  readonly powerPercent: number;
  readonly carryMeters: number;
  readonly totalMeters: number;
  readonly selectedClub: ClubDefinition;
  readonly surface: string;
  readonly speedMetersPerSecond: number;
  readonly slopeReadEnabled: boolean;
  readonly landingZonePreviewEnabled: boolean;
  readonly distanceToPinYards: number;
  readonly strokes: number;
  readonly holed: boolean;
  readonly holeOutAnimating: boolean;
  readonly holeNumber: number;
  readonly par: number;
  readonly yardage: number;
  readonly roundStatus: RoundStatus;
  readonly isFinalHole: boolean;
  readonly resultLabel: string;
  readonly scoreRelativeToPar: number;
  readonly scorecard: readonly HoleScore[];
  readonly totalStrokes: number;
  readonly totalPar: number;
  readonly totalRelativeToPar: number;
  readonly transitionActive: boolean;
  readonly trajectory: TrajectoryDefinition;
  readonly selectedTrajectory: TrajectoryId;
  readonly wind: WindSnapshot;
  readonly shotFeedback: string;
  readonly currentCompetitorName: string;
  readonly currentTurnType: 'human' | 'ai';
  readonly opponentName: string;
  readonly opponentScore: number;
  readonly opponentCurrentHoleStrokes: number;
  readonly opponentRelativeToPar: number;
  readonly opponentDistanceToPinYards: number;
  readonly opponentLie: string;
  readonly opponentResult: string;
  readonly opponentScores: readonly (number | null)[];
  readonly playerScores: readonly (number | null)[];
}

export interface ShotDebugHudMetrics {
  readonly aimDegrees: number;
  readonly powerPercent: number;
  readonly carryMeters: number;
  readonly totalMeters: number;
  readonly selectedClub: ClubDefinition;
  readonly waitingForShot: boolean;
  readonly slopeReadEnabled: boolean;
  readonly landingZonePreviewEnabled: boolean;
  readonly distanceToPinYards: number;
  readonly strokes: number;
  readonly holed: boolean;
  readonly holeOutAnimating: boolean;
  readonly holeNumber: number;
  readonly par: number;
  readonly yardage: number;
  readonly roundStatus: RoundStatus;
  readonly isFinalHole: boolean;
  readonly resultLabel: string;
  readonly scoreRelativeToPar: number;
  readonly scorecard: readonly HoleScore[];
  readonly totalStrokes: number;
  readonly totalPar: number;
  readonly totalRelativeToPar: number;
  readonly transitionActive: boolean;
  readonly trajectory: TrajectoryDefinition;
  readonly selectedTrajectory: TrajectoryId;
  readonly wind: WindSnapshot;
  readonly shotFeedback: string;
  readonly currentCompetitorName: string;
  readonly currentTurnType: 'human' | 'ai';
  readonly opponentName: string;
  readonly opponentScore: number;
  readonly opponentCurrentHoleStrokes: number;
  readonly opponentRelativeToPar: number;
  readonly opponentDistanceToPinYards: number;
  readonly opponentLie: string;
  readonly opponentResult: string;
  readonly opponentScores: readonly (number | null)[];
  readonly playerScores: readonly (number | null)[];
}

/** Brand-neutral, broadcast-inspired gameplay HUD for portrait-first play. */
export class ShotDebugHud {
  private readonly element: HTMLDivElement;
  private readonly topBar: HTMLDivElement;
  private readonly rightControls: HTMLDivElement;
  private readonly bottomCard: HTMLDivElement;
  private readonly helpPanel: HTMLDivElement;
  private readonly scorecardPanel: HTMLDivElement;
  private readonly transitionOverlay: HTMLDivElement;
  private readonly helpButton: HTMLButtonElement;
  private readonly scorecardButton: HTMLButtonElement;
  private trajectoryOpen = false;
  private readonly slopeReadButton: HTMLButtonElement;
  private readonly landingZoneButton: HTMLButtonElement;
  private state: ShotDebugHudState | null = null;
  private sheetTab: 'leaderboard' | 'scorecard' = 'leaderboard';

  constructor(
    container: HTMLElement,
    private readonly onReset: () => void,
    private readonly onPreviousClub: () => void,
    private readonly onNextClub: () => void,
    onToggleSlopeRead: () => void,
    onToggleLandingZone: () => void,
    private readonly onNextHole: () => void,
    private readonly onRestartRound: () => void,
    private readonly onTrajectory: (trajectory: TrajectoryId) => void,
    private readonly buildInfo: ShotSimulationBuildInfo = {
      version: '0.0.0',
      mode: 'development',
      controlScheme: 'keyboard + mobile swing',
    },
    private readonly tournament?: {
      active: ActiveCareerTournament;
      event: SeasonEvent;
      playerId: string;
    },
    private readonly onModalChanged: (open: boolean) => void = () => undefined,
  ) {
    this.element = document.createElement('div');
    this.topBar = document.createElement('div');
    this.rightControls = document.createElement('div');
    this.bottomCard = document.createElement('div');
    this.helpPanel = document.createElement('div');
    this.scorecardPanel = document.createElement('div');
    this.transitionOverlay = document.createElement('div');
    this.helpButton = this.createButton('i', 'Open how to play');
    this.scorecardButton = this.createButton('CARD', 'Open scorecard');
    this.slopeReadButton = this.createButton('◎\nAIM', 'Toggle green-read aim view');
    this.landingZoneButton = this.createButton('⚑\nLAND', 'Toggle landing zone preview');

    container.style.position = container.style.position || 'relative';
    container.style.touchAction = 'none';

    this.element.className = 'gameplay-hud';
    this.topBar.className = 'gameplay-scorebug';
    this.rightControls.className = 'gameplay-actions';
    this.bottomCard.className = 'shot-tray';
    this.scorecardPanel.className = 'gameplay-sheet';
    this.element.setAttribute('aria-live', 'polite');
    Object.assign(this.element.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      color: '#f8fbff',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
    });

    Object.assign(this.topBar.style, {
      position: 'absolute',
      top: 'max(10px, env(safe-area-inset-top))',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'min(460px, calc(100vw - 16px))',
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0,1fr) auto',
      overflow: 'hidden',
      borderRadius: '16px',
      background: 'linear-gradient(180deg, rgba(7, 30, 52, 0.92), rgba(4, 19, 33, 0.9))',
      border: '1px solid rgba(214, 230, 255, 0.22)',
      boxShadow: '0 18px 45px rgba(0, 0, 0, 0.28)',
      backdropFilter: 'blur(10px)',
      pointerEvents: 'auto',
    });

    Object.assign(this.rightControls.style, {
      position: 'absolute',
      right: 'max(10px, env(safe-area-inset-right))',
      top: 'auto',
      transform: 'none',
      display: 'grid',
      gap: '10px',
      pointerEvents: 'auto',
    });

    Object.assign(this.bottomCard.style, {
      position: 'absolute',
      left: '50%',
      bottom: 'max(12px, env(safe-area-inset-bottom))',
      transform: 'translateX(-50%)',
      width: 'min(330px, calc(100vw - 28px))',
      minHeight: '42px',
      borderRadius: '18px',
      background: 'linear-gradient(180deg, rgba(9, 22, 20, 0.68), rgba(5, 12, 12, 0.78))',
      border: '1px solid rgba(255, 255, 255, 0.18)',
      boxShadow: '0 16px 42px rgba(0,0,0,0.28)',
      backdropFilter: 'blur(10px)',
      pointerEvents: 'auto',
    });

    this.slopeReadButton.textContent = 'READ';
    this.landingZoneButton.textContent = 'LAND';
    [this.helpButton, this.scorecardButton, this.slopeReadButton, this.landingZoneButton].forEach(
      (button) => this.wireButton(button),
    );

    this.bindActionButton(this.slopeReadButton, onToggleSlopeRead);
    this.bindActionButton(this.landingZoneButton, onToggleLandingZone);
    this.bindActionButton(this.helpButton, () => {
      container.dispatchEvent(new CustomEvent('gameplay-pause-requested'));
    });
    this.bindActionButton(this.scorecardButton, () => this.openTournamentSheet('leaderboard'));

    this.rightControls.append(this.landingZoneButton, this.slopeReadButton);
    this.helpButton.textContent = '☰';
    this.helpButton.className = 'scorebug-menu';
    this.scorecardButton.className = 'scorebug-position';
    Object.assign(this.helpButton.style, {
      position: 'static',
      top: 'calc(max(10px, env(safe-area-inset-top)) + 8px)',
      right: 'calc(max(8px, env(safe-area-inset-right)) + 8px)',
      width: '34px',
      height: '34px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,.14)',
      border: '1px solid rgba(255,255,255,.24)',
      fontSize: '16px',
      zIndex: '2',
    });
    Object.assign(this.scorecardButton.style, {
      position: 'static',
      top: 'calc(max(10px, env(safe-area-inset-top)) + 8px)',
      left: 'calc(max(8px, env(safe-area-inset-left)) + 8px)',
      minWidth: '58px',
      height: '34px',
      borderRadius: '999px',
      background: 'rgba(255,255,255,.14)',
      border: '1px solid rgba(255,255,255,.24)',
      fontSize: '11px',
      zIndex: '2',
    });
    this.element.append(this.topBar, this.rightControls, this.bottomCard);
    this.buildHelpPanel();
    this.buildScorecardPanel();
    this.buildTransitionOverlay();
    this.element.append(this.helpPanel, this.scorecardPanel, this.transitionOverlay);
    container.appendChild(this.element);
  }

  update(state: ShotDebugHudState): void {
    this.state = state;
    const shotNumber = state.strokes + (state.phase === 'waiting' && !state.holed ? 1 : 0);
    this.topBar.innerHTML = '';
    const entry = this.tournament?.active.tournamentState.leaderboard.find(
      (row) => row.golferId === this.tournament?.playerId,
    );
    this.scorecardButton.textContent = entry
      ? `${entry.position > 1 ? 'T' : ''}${entry.position} · ${this.formatRelative(entry.total)}`
      : `You · ${this.formatRelative(state.totalRelativeToPar)}`;
    const main = document.createElement('div');
    main.className = 'scorebug-main';
    main.innerHTML = `<div class="scorebug-primary"><span>${this.tournament ? `R${this.tournament.active.tournamentState.currentRound} · ` : ''}H${state.holeNumber} · Par ${state.par}</span><strong>${state.holed ? 'Holed' : `${state.distanceToPinYards.toFixed(0)} to pin`}</strong></div><div class="scorebug-secondary"><span>${state.yardage} yd · Shot ${Math.max(1, shotNumber)}</span><span>${state.currentTurnType === 'ai' ? state.opponentName : state.surface}</span></div>`;
    this.topBar.append(this.scorecardButton, main, this.helpButton);
    this.slopeReadButton.style.background = state.slopeReadEnabled
      ? 'rgba(20, 70, 96, 0.86)'
      : 'rgba(6, 14, 16, 0.72)';
    this.landingZoneButton.style.background = state.landingZonePreviewEnabled
      ? 'rgba(20, 70, 96, 0.86)'
      : 'rgba(6, 14, 16, 0.72)';
    this.transitionOverlay.style.display = state.transitionActive ? 'grid' : 'none';
    this.bottomCard.innerHTML = state.opponentResult
      ? `<div style="display:grid;place-items:center;min-height:94px;padding:14px;text-align:center;"><div><div style="font-weight:950;font-size:18px;">${state.opponentResult}</div><div style="font-size:12px;font-weight:850;opacity:.82;margin-top:6px;">${state.opponentDistanceToPinYards.toFixed(0)} yd to pin · ${state.opponentLie}</div></div></div>`
      : state.roundStatus !== 'playing'
        ? `<div style="display:grid;place-items:center;min-height:142px;padding:14px;text-align:center;"><div><div style="font-weight:850;font-size:11px;opacity:.78;">${state.roundStatus === 'round-complete' ? 'Round Complete' : `Hole ${state.holeNumber} Complete`}</div><div style="font-weight:950;font-size:28px;line-height:1;margin-top:5px;">${state.resultLabel}</div><div style="font-size:13px;font-weight:850;opacity:.82;margin-top:7px;">Par ${state.par} · ${state.strokes} strokes · ${this.formatRelative(state.scoreRelativeToPar)}</div><div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;margin-top:12px;"><button type="button" data-scorecard="true" style="border:1px solid rgba(255,255,255,.35);border-radius:999px;background:rgba(255,255,255,.12);color:#fff;font-weight:950;padding:10px 14px;pointer-events:auto;">Scorecard</button>${state.roundStatus === 'round-complete' ? '<button type="button" data-restart-round="true" style="border:1px solid rgba(255,255,255,.35);border-radius:999px;background:#f8fbff;color:#071d33;font-weight:950;padding:10px 16px;pointer-events:auto;">Play again</button>' : '<button type="button" data-next-hole="true" style="border:1px solid rgba(255,255,255,.35);border-radius:999px;background:#f8fbff;color:#071d33;font-weight:950;padding:10px 16px;pointer-events:auto;">Next Hole</button>'}</div><button type="button" data-reset-hole="true" style="margin-top:8px;border:0;background:transparent;color:rgba(255,255,255,.8);font-weight:850;padding:6px;pointer-events:auto;">Restart hole</button></div></div>`
        : state.currentTurnType === 'ai'
          ? `<div style="display:grid;place-items:center;min-height:94px;padding:14px;text-align:center;"><div><div style="font-weight:950;font-size:22px;">${state.opponentName}</div><div style="font-size:12px;font-weight:850;opacity:.82;margin-top:6px;">Taking ${state.opponentCurrentHoleStrokes + 1}${this.ordinalSuffix(state.opponentCurrentHoleStrokes + 1)} shot · ${state.opponentDistanceToPinYards.toFixed(0)} yd</div></div></div>`
          : state.holeOutAnimating
            ? `<div style="display:grid;place-items:center;min-height:92px;padding:14px;text-align:center;"><div><div style="font-weight:950;font-size:25px;line-height:1;">Dropping...</div><div style="font-size:13px;font-weight:850;opacity:.82;margin-top:7px;">Cup captured</div></div></div>`
            : `<div style="display:grid;grid-template-columns:38px 1fr 38px;align-items:center;padding:9px 10px;text-align:center;gap:8px;">
      <button type="button" data-club-prev="true" aria-label="Previous club" style="height:30px;border-radius:999px;border:1px solid rgba(255,255,255,.26);background:rgba(255,255,255,.12);color:#fff;font-weight:950;font-size:20px;pointer-events:auto;">‹</button>
      <div style="font-weight:900;font-size:10px;letter-spacing:.06em;opacity:.92;">Club · ${this.compactClubName(state.selectedClub.name)} · ${this.clubDistanceLabel(state)}</div>
      <button type="button" data-club-next="true" aria-label="Next club" style="height:30px;border-radius:999px;border:1px solid rgba(255,255,255,.26);background:rgba(255,255,255,.12);color:#fff;font-weight:950;font-size:20px;pointer-events:auto;">›</button>
      <div style="grid-column:1 / -1;display:grid;grid-template-columns:1fr auto;gap:8px;align-items:center;pointer-events:auto;"><button type="button" data-trajectory="true" ${state.selectedClub.id === 'putter' ? 'disabled' : ''} style="height:30px;border-radius:999px;border:1px solid rgba(255,255,255,.26);background:rgba(255,255,255,.12);color:#fff;font-weight:950;font-size:11px;">TRAJ · ${state.trajectory.label}</button><div style="font-weight:900;font-size:10px;opacity:.84;white-space:nowrap;">Wind ${state.wind.speedMph.toFixed(0)} mph <span style="display:inline-block;transform:rotate(${state.wind.arrowDegrees}deg);">↑</span></div></div>
      ${this.trajectoryOpen ? '<div style="grid-column:1 / -1;display:grid;grid-template-columns:repeat(3,1fr);gap:6px;pointer-events:auto;"><button data-trajectory-choice="low" type="button">LOW</button><button data-trajectory-choice="normal" type="button">NORMAL</button><button data-trajectory-choice="high" type="button">HIGH</button></div>' : ''}
      ${state.shotFeedback ? `<div style="grid-column:1 / -1;font-weight:950;font-size:12px;letter-spacing:.08em;opacity:.9;">${state.shotFeedback}</div>` : ''}
    </div>`;
    this.bindDynamicButton('[data-reset-hole]', this.onReset);
    this.bindDynamicButton('[data-next-hole]', this.onNextHole);
    this.bindDynamicButton('[data-restart-round]', this.onRestartRound);
    this.bindDynamicButton('[data-scorecard]', () => this.setScorecardVisible(true));
    this.bindDynamicButton('[data-club-prev]', this.onPreviousClub);
    this.bindDynamicButton('[data-club-next]', this.onNextClub);
    this.bindDynamicButton('[data-trajectory]', () => {
      this.trajectoryOpen = !this.trajectoryOpen;
      if (this.state) this.update(this.state);
    });
    this.bottomCard.querySelectorAll('[data-trajectory-choice]').forEach((button) => {
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.trajectoryOpen = false;
        this.onTrajectory((button as HTMLElement).dataset.trajectoryChoice as TrajectoryId);
      });
    });
  }

  private bindDynamicButton(selector: string, onClick: () => void): void {
    const button = this.bottomCard.querySelector(selector);
    button?.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    });
    button?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });
  }

  snapshotToState(snapshot: BallStateSnapshot, metrics: ShotDebugHudMetrics): ShotDebugHudState {
    const phase: ShotDebugPhase = metrics.waitingForShot
      ? 'waiting'
      : snapshot.phase === 'bouncing'
        ? 'rolling'
        : snapshot.phase;
    return {
      phase,
      aimDegrees: metrics.aimDegrees,
      powerPercent: metrics.powerPercent,
      carryMeters: metrics.carryMeters,
      totalMeters: metrics.totalMeters,
      selectedClub: metrics.selectedClub,
      speedMetersPerSecond: snapshot.velocity.length(),
      surface: snapshot.surfaceType,
      slopeReadEnabled: metrics.slopeReadEnabled,
      landingZonePreviewEnabled: metrics.landingZonePreviewEnabled,
      distanceToPinYards: metrics.distanceToPinYards,
      strokes: metrics.strokes,
      holed: metrics.holed,
      holeOutAnimating: metrics.holeOutAnimating,
      holeNumber: metrics.holeNumber,
      par: metrics.par,
      yardage: metrics.yardage,
      roundStatus: metrics.roundStatus,
      isFinalHole: metrics.isFinalHole,
      resultLabel: metrics.resultLabel,
      scoreRelativeToPar: metrics.scoreRelativeToPar,
      scorecard: metrics.scorecard,
      totalStrokes: metrics.totalStrokes,
      totalPar: metrics.totalPar,
      totalRelativeToPar: metrics.totalRelativeToPar,
      transitionActive: metrics.transitionActive,
      trajectory: metrics.trajectory,
      selectedTrajectory: metrics.selectedTrajectory,
      wind: metrics.wind,
      shotFeedback: metrics.shotFeedback,
      currentCompetitorName: metrics.currentCompetitorName,
      currentTurnType: metrics.currentTurnType,
      opponentName: metrics.opponentName,
      opponentScore: metrics.opponentScore,
      opponentCurrentHoleStrokes: metrics.opponentCurrentHoleStrokes,
      opponentRelativeToPar: metrics.opponentRelativeToPar,
      opponentDistanceToPinYards: metrics.opponentDistanceToPinYards,
      opponentLie: metrics.opponentLie,
      opponentResult: metrics.opponentResult,
      opponentScores: metrics.opponentScores,
      playerScores: metrics.playerScores,
    };
  }

  dispose(): void {
    this.element.remove();
  }

  private createButton(text: string, label: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = text;
    button.setAttribute('aria-label', label);
    return button;
  }

  private wireButton(button: HTMLButtonElement): void {
    Object.assign(button.style, {
      border: '0',
      color: '#fff',
      fontWeight: '900',
      cursor: 'pointer',
      touchAction: 'manipulation',
      whiteSpace: 'pre-line',
      pointerEvents: 'auto',
    });
  }

  private bindActionButton(button: HTMLButtonElement, onAction: () => void): void {
    button.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      onAction();
    });
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });
  }

  private compactClubName(name: string): string {
    return name === 'Pitching Wedge' ? 'Pitch Wedge' : name;
  }

  private clubDistanceLabel(state: ShotDebugHudState): string {
    if (state.selectedClub.id === 'putter') return 'roll club';
    return `${state.selectedClub.expectedCarryYards} yd club`;
  }

  private buildHelpPanel(): void {
    Object.assign(this.helpPanel.style, {
      position: 'absolute',
      inset: '0',
      display: 'none',
      placeItems: 'center',
      background: 'rgba(0, 7, 12, 0.42)',
      pointerEvents: 'auto',
      padding: '18px',
    });
    const card = document.createElement('div');
    Object.assign(card.style, {
      width: 'min(360px, calc(100vw - 36px))',
      borderRadius: '20px',
      background: 'linear-gradient(160deg, rgba(6,29,48,.96), rgba(2,12,22,.97))',
      border: '1px solid rgba(210,230,255,.25)',
      boxShadow: '0 24px 70px rgba(0,0,0,.45)',
      padding: '18px 22px 22px',
      color: '#fff',
      textTransform: 'none',
    });
    card.innerHTML = `<div style="display:flex;justify-content:space-between;align-items:center;text-transform:uppercase;font-weight:950;letter-spacing:.04em;"><span>How to play</span><button aria-label="Close help" style="background:transparent;border:0;color:#fff;font-size:28px;">×</button></div>
      <ul style="margin:16px 0 18px;padding-left:20px;line-height:1.45;font-size:14px;">
        <li><b>Objective:</b> hole out in the fewest strokes.</li><li><b>Swing:</b> swipe down to start the backswing, then swipe up through impact.</li><li><b>Aim:</b> drag sideways or tap Aim for a composed green-read view.</li><li><b>Landing zone:</b> preview estimated carry area.</li><li><b>Trajectory:</b> choose LOW, NORMAL, or HIGH. Low punches under wind with more rollout; High launches steeper, spins more, and is wind-sensitive.</li><li><b>Wind:</b> the arrow shows where wind is blowing relative to your aim: toward you is headwind, away is tailwind, sideways is crosswind.</li><li><b>Clubs:</b> use keyboard number keys to switch clubs; the HUD shows expected carry.</li><li><b>Keyboard:</b> arrows/A-D aim, Space swings, G green read, L landing zone, R reset.</li>
      </ul><div style="font-size:11px;opacity:.55;border-top:1px solid rgba(255,255,255,.16);padding-top:10px;">Build ${this.buildInfo.version} · ${this.buildInfo.mode}</div>`;
    const close = card.querySelector('button');
    close?.addEventListener('click', () => this.setHelpVisible(false));
    close?.addEventListener('pointerdown', (event) => event.stopPropagation());
    const gotIt = this.createButton('GOT IT', 'Close help');
    Object.assign(gotIt.style, {
      width: '100%',
      minHeight: '46px',
      borderRadius: '10px',
      background: 'linear-gradient(180deg,#1f5fac,#123d7b)',
      border: '1px solid rgba(180,215,255,.55)',
    });
    this.wireButton(gotIt);
    gotIt.addEventListener('click', () => this.setHelpVisible(false));
    card.append(gotIt);
    this.helpPanel.append(card);
  }

  private buildTransitionOverlay(): void {
    Object.assign(this.transitionOverlay.style, {
      position: 'absolute',
      inset: '0',
      display: 'none',
      placeItems: 'center',
      background: 'rgba(2, 10, 18, 0.72)',
      color: '#fff',
      pointerEvents: 'none',
      fontWeight: '950',
      fontSize: 'clamp(24px, 7vw, 38px)',
      letterSpacing: '.08em',
    });
    this.transitionOverlay.textContent = 'Next tee';
  }

  private buildScorecardPanel(): void {
    setSheetOpen(this.scorecardPanel, false);
  }

  private setScorecardVisible(visible: boolean): void {
    if (visible) this.openTournamentSheet('scorecard');
    else this.closeTournamentSheet();
  }

  openTournamentSheet(tab: 'leaderboard' | 'scorecard' = 'leaderboard'): void {
    this.sheetTab = tab;
    setSheetOpen(this.scorecardPanel, true);
    this.scorecardPanel.setAttribute('role', 'dialog');
    this.scorecardPanel.setAttribute('aria-modal', 'true');
    this.onModalChanged(true);
    this.renderScorecard();
  }

  closeTournamentSheet(): void {
    setSheetOpen(this.scorecardPanel, false);
    this.onModalChanged(false);
  }

  private renderScorecard(): void {
    if (!this.state) return;
    const renderRows = (holes: readonly HoleScore[]) =>
      holes
        .map(
          (hole) =>
            `<tr><td>${hole.holeNumber}</td><td>${hole.par}</td><td>${hole.yardage}</td><td>${this.state?.playerScores[hole.holeNumber - 1] ?? (hole.completed ? hole.strokes : '—')}</td><td>${this.state?.opponentScores[hole.holeNumber - 1] ?? '—'}</td></tr>`,
        )
        .join('');
    const subtotal = (label: string, holes: readonly HoleScore[]) =>
      `<tr style="font-weight:950;border-top:1px solid rgba(255,255,255,.16);"><td>${label}</td><td>${holes.reduce((sum, hole) => sum + hole.par, 0)}</td><td></td><td>${holes.reduce((sum, hole) => sum + (hole.completed ? hole.strokes : 0), 0)}</td><td></td></tr>`;
    const front = this.state.scorecard.slice(0, 9);
    const back = this.state.scorecard.slice(9, 18);
    const rows = [
      renderRows(front),
      subtotal('Out', front),
      renderRows(back),
      subtotal('In', back),
    ].join('');
    const tournament = this.tournament;
    const broadcast = tournament?.active.latestBroadcast;
    // The Career callback opens this sheet before the HUD's next animation-frame refresh.
    // Use the persisted checkpoint as the authoritative flow state instead of comparing
    // two independently refreshed hole counters; a strict equality check could turn the
    // post-hole sheet into a normal leaderboard and remove its only advance action.
    const isPostHole = !!broadcast && tournament?.active.checkpointReason === 'holeCompleted';
    const leaderboard = tournament
      ? tournament.active.tournamentState.leaderboard
          .map((entry) => {
            const movement = movementSince(tournament.active.priorHoleLeaderboardSnapshot, entry);
            const favorite = tournament.active.forecast.favorites.some(
              (candidate) => candidate.golferId === entry.golferId,
            );
            const defending =
              tournament.active.forecast.defendingChampion?.golferId === entry.golferId;
            return `${tournament.active.tournamentState.projectedCut !== null && entry.position === tournament.event.cutRule.topAndTies + 1 ? `<div class="cut-line">Projected cut · ${this.formatRelative(tournament.active.tournamentState.projectedCut!)}</div>` : ''}<div class="leader-row ${entry.isPlayer ? 'player' : ''} ${entry.golferId === tournament.active.pairedOpponentId ? 'opponent' : ''}"><b>${entry.position > 1 ? 'T' : ''}${entry.position}<small style="display:block">${movementMarker(movement)}</small></b><span><span class="leader-name-full">${entry.golferName}</span><span class="leader-name-short">${entry.golferShortName ?? entry.golferName}</span>${favorite ? '<small class="story-badge">FAV</small>' : defending ? '<small class="story-badge">DEF</small>' : ''}<small style="display:block;opacity:.65">R${entry.currentRound} · ${entry.cutStatus}</small></span><b>${this.formatRelative(entry.total)}</b><span>${entry.finished ? 'F' : entry.currentHole || '—'}</span></div>`;
          })
          .join('')
      : '<p>Leaderboard is available in Career Mode.</p>';
    const content =
      this.sheetTab === 'leaderboard'
        ? leaderboard
        : `<table class="scorecard-table"><thead><tr><th>Hole</th><th>Par</th><th>Yds</th><th>You</th><th>AI</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><th>Total</th><th>${this.state.totalPar}</th><th></th><th>${this.state.totalStrokes}</th><th>${this.state.opponentScore}</th></tr></tfoot></table>`;
    this.scorecardPanel.innerHTML = `<section class="gameplay-sheet-card">${isPostHole ? `<div class="broadcast-head"><small>HOLE ${this.state.holeNumber} COMPLETE</small><b>${this.state.resultLabel} · ${this.formatRelative(this.state.scoreRelativeToPar)}</b><p>“${broadcast.headline}”</p></div>` : ''}<div class="sheet-head"><div><b>${tournament?.event.name ?? 'Round'}</b><small style="display:block;opacity:.7">${tournament?.event.courseName ?? `Hole ${this.state.holeNumber}`}</small></div>${isPostHole ? '' : '<button type="button" data-close-scorecard aria-label="Close tournament sheet">×</button>'}</div><div class="sheet-tabs"><button data-tab="leaderboard" aria-selected="${this.sheetTab === 'leaderboard'}">Leaderboard</button><button data-tab="scorecard" aria-selected="${this.sheetTab === 'scorecard'}">Scorecard</button></div><div class="sheet-scroll">${content}</div>${isPostHole ? '<div class="broadcast-actions"><button type="button" data-next-hole>Next Hole</button></div>' : ''}</section>`;
    this.bindPanelButton(
      '[data-close-scorecard]',
      () => this.setScorecardVisible(false),
      this.scorecardPanel,
    );
    this.bindPanelButton(
      '[data-next-hole]',
      () => {
        if (tournament)
          tournament.active.priorHoleLeaderboardSnapshot = captureLeaderboardSnapshot(
            tournament.active.tournamentState,
            this.state!.holeNumber + 1,
          );
        if (tournament) tournament.active.latestBroadcast = undefined;
        this.closeTournamentSheet();
        this.onNextHole();
      },
      this.scorecardPanel,
    );
    this.bindPanelButton(
      '[data-tab="leaderboard"]',
      () => this.openTournamentSheet('leaderboard'),
      this.scorecardPanel,
    );
    this.bindPanelButton(
      '[data-tab="scorecard"]',
      () => this.openTournamentSheet('scorecard'),
      this.scorecardPanel,
    );
  }

  private bindPanelButton(selector: string, onClick: () => void, root: HTMLElement): void {
    const button = root.querySelector(selector);
    button?.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    });
    button?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });
  }

  private ordinalSuffix(value: number): string {
    if (value % 100 >= 11 && value % 100 <= 13) return 'th';
    return value % 10 === 1 ? 'st' : value % 10 === 2 ? 'nd' : value % 10 === 3 ? 'rd' : 'th';
  }

  private formatRelative(relative: number): string {
    if (relative === 0) return 'E';
    return relative > 0 ? `+${relative}` : String(relative);
  }

  private setHelpVisible(visible: boolean): void {
    this.helpPanel.style.display = visible ? 'grid' : 'none';
    if (visible && this.state) this.update(this.state);
  }
}
